import React, { Component } from "react";
import { connect } from "react-redux";
import { Label, Table } from "semantic-ui-react";
import { getHistoricalMetricsByProvider } from "../../redux/modules/payReports";
import Loading from "../Loading";
import {getPercentageOfBillableCare} from "../../utils/commonutils";

class HistoricalMetrics extends Component {
    state = { data: [] };

    componentDidMount() {
        const { dispatch, doctorABBR } = this.props;

        dispatch(getHistoricalMetricsByProvider({ abbr: doctorABBR }))
            .then(res => {
                const rows = Array.isArray(res) ? res : res?.data || [];
                this.setState({ data: rows });
            });
    }


    parseMonth(str) {
        const d = new Date(str);
        return isNaN(d) ? null : new Date(d.getFullYear(), d.getMonth(), 1);
    }

    formatMonth(date) {
        return new Intl.DateTimeFormat("en-US", {
            month: "long",
            year: "numeric"
        }).format(date);
    }

    getMonthRange(start, end) {
        const months = [];
        const cur = new Date(start);

        while (cur <= end) {
            months.push(this.formatMonth(cur));
            cur.setMonth(cur.getMonth() + 1);
        }
        return months;
    }

    aggregateByMonth(arr) {
        const map = {};

        arr.forEach(item => {
            const m = item.payMonth;
            if (!map[m]) {
                map[m] = {
                    workDays: 0,
                    totalPay: 0,
                    finNote: 0,
                    finNoteBcare: 0,
                    basePay : 0,
                    bCare : 0,
                    patVisits: 0,
                    // Over-base is a provider-level concept that only applies to base
                    // contracts. Capture the persisted flag + contractType from the
                    // first base-contract row for the month so getBasePayStatus can
                    // label it (non-base rows leave these null → '-').
                    isPracticeOverBase: null,
                    contractType: null
                };
            }
            map[m].workDays += Number(item.workDays) || 0;
            map[m].totalPay += Number(item.totalPay) || 0;
            map[m].finNote += Number(item.finNote) || 0;
            map[m].finNoteBcare += Number(item.finNoteBcare) || 0;
            map[m].basePay += Number(item.basePay) || 0;
            map[m].bCare += Number(item.bCare) || 0;
            map[m].patVisits += Number(item.patVisits) || 0;

            if (map[m].isPracticeOverBase == null
                && item.isPracticeOverBase != null
                && this.isBaseContract(item.contractType)) {
                map[m].isPracticeOverBase = item.isPracticeOverBase;
                map[m].contractType = item.contractType;
            }
        });

        // bCPV = bCare / patient visits across all practices for the month,
        // matching the bCPV column on CdpReports.
        Object.values(map).forEach(row => {
            row.bCPV = row.patVisits ? row.bCare / row.patVisits : 0;
        });

        return map;
    }

    fmt(v) {
        return v == null ? "-" : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    // Over/Base only applies to base contracts (Greater Of family / Collections /
    // GOX / GOZ). Sum Of, UKG-HYN, hygienists, etc. have no over-base concept.
    // Keyed off contractType (not the base amount) to match the History Report,
    // because displayBasePay is absent from older saved reports.
    isBaseContract(contractType) {
        const ct = (contractType || '').toLowerCase();
        return ct.includes('greater of') || ct.includes('collections')
            || ct.includes('gox') || ct.includes('goz');
    }

    // Derive the Base / Over Base status for a month from the persisted
    // provider-level over-base flag (isProviderOverBase, stored as
    // isPracticeOverBase) — the same flag CdpReports and the History Report show.
    getBasePayStatus(agg, month) {
        const row = agg[month];
        if (!row || row.isPracticeOverBase == null) return null;
        if (!this.isBaseContract(row.contractType)) return null;
        return row.isPracticeOverBase ? 'Over Base' : 'Base Pay';
    }

    renderRow(label, months, agg, field = null) {
        return (
            <Table.Row>
                <Table.Cell>{label}</Table.Cell>
                {months.map(m => (
                    <Table.Cell key={m}>
                        {field ? this.fmt(agg[m]?.[field]) : "-"}
                    </Table.Cell>
                ))}
            </Table.Row>
        );
    }

    getBillablePercentForMonth(agg, month) {
        const row = agg[month];
        if (!row) return null;

        return getPercentageOfBillableCare(
            row.totalPay,
            row.bCare,
            row.finNoteBcare
        );
    }




    render() {
        const { isLoad } = this.props;
        const { data } = this.state;

        if (isLoad) return <Loading />;
        if (!data.length) return <div>No data available</div>;

        const parsedMonths = data
            .map(d => this.parseMonth(d.payMonth))
            .filter(Boolean);

        if (!parsedMonths.length) {
            return <div>Invalid month data</div>;
        }

        const minMonth = new Date(Math.min(...parsedMonths));
        const maxMonth = new Date(Math.max(...parsedMonths));
        const months = this.getMonthRange(minMonth, maxMonth);
        const agg = this.aggregateByMonth(data);

        return (
            <Table celled fixed>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Metric</Table.HeaderCell>
                        {months.map(m => (
                            <Table.HeaderCell key={m}>
                                {m}
                            </Table.HeaderCell>
                        ))}
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    <Table.Row>
                        <Table.Cell>Base / Overbase</Table.Cell>
                        {months.map(m => {
                            const status = this.getBasePayStatus(agg, m);
                            return (
                                <Table.Cell key={m}>
                                    {status
                                        ? <Label
                                            size="tiny"
                                            color={status === 'Over Base' ? 'green' : 'yellow'}
                                        >
                                            {status}
                                        </Label>
                                        : "-"}
                                </Table.Cell>
                            );
                        })}
                    </Table.Row>
                    {this.renderRow("Worked Days", months, agg, "workDays")}
                    {this.renderRow("Base Amount", months, agg, "basePay")}
                    {this.renderRow("bCPV", months, agg, "bCPV")}
                    {this.renderRow("FN", months, agg, "finNote")}
                    {this.renderRow("FN-bCARE", months, agg, "finNoteBcare")}
                    {this.renderRow("Total Pay", months, agg, "totalPay")}
                    <Table.Row>
                        <Table.Cell>% of Billable</Table.Cell>
                        {months.map(m => (
                            <Table.Cell key={m}>
                                {this.fmt(this.getBillablePercentForMonth(agg, m))}%
                            </Table.Cell>
                        ))}
                    </Table.Row>
                </Table.Body>
            </Table>
        );
    }
}

export default connect(state => ({
    isLoad: state.get("payReports").get("isPrePayLoad")
}))(HistoricalMetrics);
